import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Pencil, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '@/hooks/useApi';
import { useMe } from '@/hooks/useMe';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/ui/pill';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { parseSocialHandle, type SocialChannel } from '@/lib/socialHandle';
import { inviteStage, verdictLabel, verdictTone } from '@/lib/labels';
import { formatDate, formatDateTime } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import {
  HANDLE_RECHECK,
  INVALID_HANDLE,
  channelLabel,
  inviteEarned,
  inviteSourceLabel,
  stageAge,
  stillToBePaid,
} from '@/lib/outreach-copy';
import type { InvitePost, Me, OutreachInvite } from '@/api/types';
import { InfoHint } from '@/components/ui/InfoHint';
import type { HintKey } from '@/lib/infoHints';
import { OwedBreakdownPanel } from '@/components/invites/OwedBreakdownPanel';

const HANDLE_EDITABLE_CHANNELS = new Set<string>(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'TWITTER']);

type OutreacherMe = Extract<Me, { role: 'outreacher' }>;
function asOutreacher(me: Me | undefined): OutreacherMe | null {
  return me && me.role === 'outreacher' ? me : null;
}

export function InviteDetailPage(): JSX.Element {
  const { publicId } = useParams<{ publicId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const me = useMe();
  const outreacher = asOutreacher(me.data);
  const teamTracks = outreacher?.teamSettings.clippingResolveRequired;

  const inviteQuery = useQuery({
    enabled: !!publicId,
    queryKey: ['invite', publicId],
    queryFn: () => api.get<OutreachInvite>(`/portal/invites/${publicId}`),
  });

  const screenshotUrlQuery = useQuery({
    enabled: !!inviteQuery.data?.screenshotKey,
    queryKey: ['invite-screenshot', publicId],
    queryFn: () => api.get<{ url: string }>(`/portal/invites/${publicId}/screenshot`).then((v) => v.url),
  });

  const postsQuery = useQuery({
    enabled: !!inviteQuery.data?.matchedUserId,
    queryKey: ['invite-posts', publicId],
    queryFn: () =>
      api.get<{ posts: InvitePost[]; unavailable?: boolean }>(`/portal/invites/${publicId}/posts`),
    staleTime: 60_000,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.upload<OutreachInvite>(`/portal/invites/${publicId}/screenshot`, file),
    onSuccess: () => {
      toast.success('Screenshot uploaded');
      void queryClient.invalidateQueries({ queryKey: ['invite', publicId] });
      void queryClient.invalidateQueries({ queryKey: ['invite-screenshot', publicId] });
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editHandleMutation = useMutation({
    mutationFn: (targetIdentifier: string) =>
      api.patch<OutreachInvite>(`/portal/invites/${publicId}`, { targetIdentifier }),
    onSuccess: (inv) => {
      toast.success(`Saved as @${inv.targetIdentifier}. ${HANDLE_RECHECK}`);
      void queryClient.invalidateQueries({ queryKey: ['invite', publicId] });
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const [editingHandle, setEditingHandle] = useState(false);
  const [draftHandle, setDraftHandle] = useState('');

  if (inviteQuery.isLoading) {
    return (
      <div className="flex items-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (inviteQuery.error || !inviteQuery.data) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">We couldn't find this invite.</p>
        <Link to="/portal/invites" className="text-sm text-primary underline-offset-2 hover:underline">
          Back to My creators
        </Link>
      </div>
    );
  }

  const invite = inviteQuery.data;
  const stage = inviteStage(invite, { teamTracks });
  const age = stageAge(invite, { teamTracks });
  const isRejected = invite.status === 'REJECTED';
  const isHidden = invite.deletedAt !== null;
  const paid = Number.parseFloat(invite.amountPaid);
  const followerBonus = Number.parseFloat(invite.bracketBonusAttributed || '0');
  const canEditHandle =
    !isRejected &&
    !isHidden &&
    !(Number.isFinite(paid) && paid > 0) &&
    HANDLE_EDITABLE_CHANNELS.has(invite.channel);
  const manualTracking = teamTracks === false;
  const joined = invite.status === 'VERIFIED' || invite.status === 'CONVERTED';
  const checked = invite.lastResolvedAt !== null;

  function startEditHandle(): void {
    setDraftHandle(`@${invite.targetIdentifier}`);
    setEditingHandle(true);
  }
  function submitEditHandle(): void {
    const parsed = parseSocialHandle(invite.channel as SocialChannel, draftHandle);
    if (!parsed) {
      toast.error(INVALID_HANDLE);
      return;
    }
    if (parsed.username === invite.targetIdentifier) {
      setEditingHandle(false);
      return;
    }
    editHandleMutation.mutate(parsed.username, { onSuccess: () => setEditingHandle(false) });
  }

  const yesNo = (v: boolean | null | undefined): string =>
    v === true ? 'Yes' : v === false ? 'No' : 'Not checked yet';

  return (
    <div className="space-y-6">
      <Link
        to="/portal/invites"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> My creators
      </Link>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {editingHandle ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label="New handle"
                value={draftHandle}
                onChange={(e) => setDraftHandle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitEditHandle();
                  } else if (e.key === 'Escape') {
                    setEditingHandle(false);
                  }
                }}
                placeholder="@name or a profile link"
                className="w-[28rem] max-w-full"
              />
              <Button onClick={submitEditHandle} disabled={editHandleMutation.isPending || !draftHandle.trim()}>
                {editHandleMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditingHandle(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight">@{invite.targetIdentifier}</h1>
              <InviteStagePill invite={invite} teamTracks={teamTracks} />
              {canEditHandle && (
                <Button
                  size="icon"
                  variant="ghost"
                  title="Edit handle (typo or wrong account)"
                  aria-label="Edit handle"
                  onClick={startEditHandle}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
        {editingHandle && <p className="text-xs text-muted-foreground">{HANDLE_RECHECK}</p>}
        <p className="text-sm text-muted-foreground">
          {channelLabel(invite.channel)} · Logged {formatDate(invite.createdAt)} ·{' '}
          {inviteSourceLabel(invite.discoveryFolderName)}
          {age.text && stage.key !== 'joined' && stage.key !== 'notPaid' ? ` · ${age.text}` : ''}
        </p>
        <p className="max-w-[65ch] text-sm">{stage.hint}</p>
        {manualTracking && !isRejected && !isHidden && (
          <p className="max-w-[65ch] text-sm text-muted-foreground">
            Your team isn't checked automatically with clipping. An admin updates joins and posts for
            your invites, so the numbers below can lag.
          </p>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Money</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {isRejected ? (
              <>
                <p className="text-muted-foreground">
                  Not paid. An admin turned this invite down, so nothing more will be paid on it.
                </p>
                <Row
                  label="Paid"
                  hintKey="amountPaid"
                  value={`$${formatMoney(invite.amountPaid)}`}
                />
                <Row label="Still to be paid" hintKey="netOwed" value={`$${formatMoney(0)}`} />
              </>
            ) : (
              <>
                <Row
                  label="Earned"
                  hintKey="lockedValue"
                  value={`$${formatMoney(inviteEarned(invite))}`}
                />
                <Row
                  label="Paid"
                  hintKey="amountPaid"
                  value={`$${formatMoney(invite.amountPaid)}`}
                />
                <Row
                  label="Still to be paid"
                  hintKey="netOwed"
                  value={`$${formatMoney(stillToBePaid(invite))}`}
                />
                {followerBonus > 0 && (
                  <Row
                    label="Follower bonus"
                    hintKey="bracketBonus"
                    value={`$${formatMoney(invite.bracketBonusAttributed)}`}
                  />
                )}
              </>
            )}
            {isHidden && (
              <p className="pt-1 text-xs text-muted-foreground">
                You hid this invite. Admins still see it, and any amount earned stays until an admin
                writes it off.
              </p>
            )}
            {/* SPA-1208 + transparency: fetched on-demand (`enabled: open`) so
                invites that are paid-in-full don't pay the extra round-trip.
                See src/components/invites/OwedBreakdownPanel.tsx. */}
            {publicId && !isRejected ? (
              <OwedBreakdownPanel
                publicId={publicId}
                lockedValue={invite.lockedValue ?? '0'}
                amountPaid={invite.amountPaid}
                stillOwed={stillToBePaid(invite)}
              />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {invite.channel === 'DISCORD' ? (
              <Row label="Joined Discord" value={yesNo(invite.isInServer)} />
            ) : (
              <Row label="Joined clipping" value={joined ? 'Yes' : checked ? 'No' : 'Not checked yet'} />
            )}
            <Row
              label="Posts that count for you"
              hint="Approved posts made after you logged them."
              value={invite.postCount.toLocaleString()}
            />
            <Row
              label="Followers"
              value={invite.followerCount !== null ? invite.followerCount.toLocaleString() : '—'}
            />
            <Row
              label="Last checked"
              value={invite.lastResolvedAt ? formatDateTime(invite.lastResolvedAt) : 'Not checked yet'}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Proof</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {invite.screenshotKey && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Pill tone={verdictTone(invite.screenshotVerdict)}>{verdictLabel(invite.screenshotVerdict)}</Pill>
              {invite.screenshotReviewedAt && (
                <span className="text-muted-foreground">Reviewed {formatDate(invite.screenshotReviewedAt)}</span>
              )}
            </div>
          )}
          {invite.screenshotReviewNote && (
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <span className="font-medium">Reviewer note: </span>
              {invite.screenshotReviewNote}
            </p>
          )}
          {screenshotUrlQuery.data ? (
            <img
              src={screenshotUrlQuery.data}
              alt={`Screenshot of the reply from @${invite.targetIdentifier}`}
              className="max-h-96 rounded-md border"
            />
          ) : invite.screenshotKey && screenshotUrlQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading screenshot…</p>
          ) : !invite.screenshotKey ? (
            <p className="text-sm text-muted-foreground">
              No screenshot yet. Add one showing the creator asking how to join.
              {outreacher?.teamSettings.screenshotRequired ? ' Your team needs it to pay this invite.' : ''}
            </p>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadMutation.mutate(file);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {invite.screenshotKey ? 'Replace screenshot' : 'Upload screenshot'}
          </Button>
        </CardContent>
      </Card>

      {invite.matchedUserId && (
        <Card>
          <CardHeader>
            <CardTitle>Clips by this creator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Only approved posts made after you logged them count for you.
            </p>
            {postsQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading posts…
              </div>
            ) : postsQuery.isError || postsQuery.data?.unavailable ? (
              <p className="text-sm text-muted-foreground">Couldn't reach clipping right now. Try again later.</p>
            ) : !postsQuery.data?.posts?.length ? (
              <p className="text-sm text-muted-foreground">No clips yet.</p>
            ) : (
              <div className="overflow-x-auto rounded border border-border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Post</th>
                      <th className="px-3 py-2 text-left font-medium">Campaign</th>
                      <th className="px-3 py-2 text-right font-medium">Views</th>
                      <th className="px-3 py-2 text-right font-medium">Likes</th>
                      <th className="px-3 py-2 text-right font-medium">Comments</th>
                      <th className="px-3 py-2 text-right font-medium">Engagement</th>
                      <th className="px-3 py-2 text-left font-medium">Posted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postsQuery.data.posts.map((post) => (
                      <tr key={post.postId} className="border-t border-border/50">
                        <td className="px-3 py-2">
                          {post.postUrl ? (
                            <a
                              href={post.postUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              {channelLabel(post.platform.toUpperCase())}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">{channelLabel(post.platform.toUpperCase())}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{post.campaignName ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {post.views != null ? fmtCompact(post.views) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {post.likes != null ? fmtCompact(post.likes) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {post.comments != null ? fmtCompact(post.comments) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {post.engagementRate != null ? `${(post.engagementRate * 100).toFixed(1)}%` : '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{formatDate(post.postedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function Row({
  label,
  value,
  hint,
  hintKey,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  hintKey?: HintKey;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 border-b pb-1 last:border-0">
      <span className="text-muted-foreground inline-flex items-center gap-1" title={hint}>
        {label}
        {hintKey ? <InfoHint hintKey={hintKey} variant="inline" /> : null}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
